/**
 * Socket listeners for the recognition feature.
 *
 * Two events of interest:
 *   - `kudos-given`     → company:{X} room. Prepend the entry into the feed
 *                         if we're looking at that company; bump the receiver's
 *                         karma in our local snapshot when it concerns us.
 *   - `kudos-received`  → user:{myId} room. The receiver gets this even when
 *                         viewing a different company, so we surface it as a
 *                         toast (and bump that company's unread badge if it's
 *                         not the active one).
 *
 * The user's `user:{id}` room is joined automatically by `sockets/subscribe`
 * at panel mount, so we don't need to re-subscribe here. The company room
 * is also joined by the presence subscribe — that's what makes the
 * `kudos-given` events visible.
 */
import { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { AppDispatch } from "configureStore";
import { socket } from "services/socket-client";
import { useAppSelector } from "hooks";
import {
  applyKudosGiven,
  fetchSnapshotThunk,
} from "./recognition.slice";
import { PointsTransaction } from "types/Points";

interface KudosGivenPayload {
  companyId: string;
  transaction: PointsTransaction;
}

interface KudosReceivedPayload {
  companyId: string;
  transaction: PointsTransaction;
}

export function useRecognitionSocket(companyId: string | undefined) {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const session = useAppSelector((state) => state.authReducer.session);
  const myUserId = session?.id ? String(session.id) : null;

  useEffect(() => {
    if (!companyId) return;

    const onKudosGiven = (...args: unknown[]) => {
      const payload = args[0] as KudosGivenPayload | undefined;
      if (!payload?.transaction || !payload.companyId) return;
      // Only forward into local state if it's THIS company. The user:{id}
      // path covers the cross-company "you got kudos" case below.
      if (String(payload.companyId) !== String(companyId)) return;
      dispatch(
        applyKudosGiven({
          companyId: payload.companyId,
          transaction: payload.transaction,
          selfId: myUserId || undefined,
        })
      );
    };

    const onKudosReceived = (...args: unknown[]) => {
      const payload = args[0] as KudosReceivedPayload | undefined;
      if (!payload?.transaction || !payload.companyId || !myUserId) return;
      // Defence in depth: server only sends this to user:{myId}, but verify.
      if (String(payload.transaction.toUser.id) !== myUserId) return;

      const eventCompanyId = String(payload.companyId);
      const isCurrent = String(eventCompanyId) === String(companyId);
      const senderName = payload.transaction.fromUser.name;

      const bolts = "⚡".repeat(payload.transaction.amount);
      toast.success(
        t("kudos_received_from_amount", {
          name: senderName,
          bolts,
          defaultValue: `${senderName} gave you ${bolts}`,
        }),
        {
          description: payload.transaction.reason,
          action: !isCurrent
            ? {
                label: t("open"),
                onClick: () => navigate(`/app/${eventCompanyId}/recognition`),
              }
            : undefined,
        }
      );

      if (!isCurrent) {
        dispatch(
          applyKudosGiven({
            companyId: eventCompanyId,
            transaction: payload.transaction,
            selfId: myUserId,
          })
        );
      }

      // Refresh snapshot for the active company so the karma counter ticks
      // up immediately (the cross-company case will fetch on next visit).
      if (isCurrent) {
        dispatch(fetchSnapshotThunk(eventCompanyId));
      }
    };

    socket.on("kudos-given", onKudosGiven as any);
    socket.on("kudos-received", onKudosReceived as any);

    return () => {
      socket.off("kudos-given", onKudosGiven as any);
      socket.off("kudos-received", onKudosReceived as any);
    };
  }, [companyId, myUserId, dispatch, navigate, t]);
}
